<script setup lang="ts">
import { computed, ref } from 'vue';
import type { EntitySummary } from '../../types/entities';
import type { ExportEntry } from '../../types/export';
import { chapterExportScenes, moveExportEntry } from '../../lib/exportSelection';
import ExportSceneSegments from './ExportSceneSegments.vue';
import { updateExportSegments } from '../../lib/exportSegments';

const props = defineProps<{ entries: ExportEntry[]; entities: EntitySummary[]; busy: boolean }>();
const emit = defineEmits<{ update: [entries: ExportEntry[]]; reorder: [entries: ExportEntry[]] }>();
const dragged = ref<string | null>(null);
const rows = computed(() => {
  const byId = new Map(props.entities.map((entity) => [entity.id, entity]));
  const seen = new Set<string>();
  return props.entries.map((entry) => {
    const entity = byId.get(entry.entityId);
    const duplicate = seen.has(entry.entityId);
    seen.add(entry.entityId);
    const scenes =
      entity?.templateCategory === 'kapitel'
        ? chapterExportScenes(entity, props.entities).map((scene) => {
            const selected = entry.sceneIds === undefined || entry.sceneIds.includes(scene.id);
            const alreadyIncluded = seen.has(scene.id);
            if (selected) seen.add(scene.id);
            return { scene, selected, alreadyIncluded };
          })
        : [];
    return { entry, entity, duplicate, scenes };
  });
});
function move(id: string, index: number) {
  if (!props.busy) emit('reorder', moveExportEntry(props.entries, id, index));
}
function remove(id: string) {
  if (!props.busy)
    emit(
      'update',
      props.entries.filter((entry) => entry.entityId !== id)
    );
}
function toggleScene(entry: ExportEntry, sceneId: string, selected: boolean) {
  if (props.busy) return;
  const chapter = props.entities.find((entity) => entity.id === entry.entityId);
  if (!chapter) return;
  const ids = new Set(entry.sceneIds ?? chapterExportScenes(chapter, props.entities).map((scene) => scene.id));
  if (selected) ids.add(sceneId);
  else ids.delete(sceneId);
  emit(
    'update',
    props.entries.map((item) =>
      item.entityId === entry.entityId
        ? {
            ...item,
            sceneIds: [...ids],
            segments: item.segments?.filter((selection) => ids.has(selection.sceneId))
          }
        : item
    )
  );
}
function selectSegments(entry: ExportEntry, sceneId: string, ids: string[] | undefined) {
  if (!props.busy)
    emit(
      'update',
      props.entries.map((item) => (item.entityId === entry.entityId ? updateExportSegments(item, sceneId, ids) : item))
    );
}
function startDrag(event: DragEvent, id: string) {
  if (props.busy) {
    event.preventDefault();
    return;
  }
  dragged.value = id;
  event.dataTransfer?.setData('text/plain', id);
}
function drop(index: number) {
  if (dragged.value) move(dragged.value, index);
  dragged.value = null;
}
</script>

<template>
  <section class="export-selection" aria-label="Exportauswahl">
    <h3>Reihenfolge im Dokument</h3>
    <p>Einträge ziehen oder mit den Pfeilen verschieben. Jede Entität erscheint einmal, an ihrer ersten Position.</p>
    <ol class="export-selection-list">
      <li
        v-for="(row, index) in rows"
        :key="row.entry.entityId"
        :draggable="!busy"
        @dragstart="startDrag($event, row.entry.entityId)"
        @dragend="dragged = null"
        @dragover.prevent
        @drop.prevent="drop(index)"
      >
        <div class="export-entry-header">
          <span class="export-position">{{ index + 1 }}</span
          ><strong>{{ row.entity?.title ?? 'Nicht mehr verfügbar' }}</strong>
          <div class="export-entry-actions">
            <button
              type="button"
              :disabled="busy || index === 0"
              :aria-label="`${row.entity?.title} nach oben`"
              @click="move(row.entry.entityId, index - 1)"
            >
              ↑</button
            ><button
              type="button"
              :disabled="busy || index === entries.length - 1"
              :aria-label="`${row.entity?.title} nach unten`"
              @click="move(row.entry.entityId, index + 1)"
            >
              ↓</button
            ><button
              type="button"
              :disabled="busy"
              :aria-label="`${row.entity?.title} entfernen`"
              @click="remove(row.entry.entityId)"
            >
              ×
            </button>
          </div>
        </div>
        <small v-if="row.duplicate" class="export-duplicate">Bereits vorher im Dokument enthalten.</small>
        <ExportSceneSegments
          v-if="row.entity?.templateCategory === 'szene' && !row.duplicate"
          :scene-id="row.entry.entityId"
          :selected-ids="row.entry.segments?.find((item) => item.sceneId === row.entry.entityId)?.segmentIds"
          :busy="busy"
          @update="selectSegments(row.entry, row.entry.entityId, $event)"
        />
        <div v-if="row.entity?.templateCategory === 'kapitel'" class="export-scene-list">
          <div v-for="item in row.scenes" :key="item.scene.id">
            <label
              ><input
                type="checkbox"
                :checked="item.selected"
                :disabled="busy"
                @change="toggleScene(row.entry, item.scene.id, ($event.target as HTMLInputElement).checked)"
              /><span
                >{{ item.scene.title
                }}<small v-if="item.selected && item.alreadyIncluded">Bereits vorher enthalten</small></span
              ></label
            >
            <ExportSceneSegments
              v-if="item.selected && !item.alreadyIncluded"
              :scene-id="item.scene.id"
              :selected-ids="row.entry.segments?.find((selection) => selection.sceneId === item.scene.id)?.segmentIds"
              :busy="busy"
              @update="selectSegments(row.entry, item.scene.id, $event)"
            />
          </div>
          <small v-if="!row.scenes.length">Keine aktiven Szenen. Der Kapitelinhalt wird exportiert.</small>
        </div>
      </li>
    </ol>
    <p v-if="!entries.length" class="export-empty">Füge links Entitäten hinzu.</p>
  </section>
</template>

<style scoped>
.export-selection {
  min-width: 0;
}
.export-selection h3 {
  margin: 0 0 8px;
}
.export-selection > p {
  font-size: 12px;
  line-height: 1.5;
  color: var(--ui-text-muted);
}
.export-selection-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 8px;
  max-height: 52vh;
  overflow: auto;
}
.export-selection-list > li {
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-md);
  background: var(--ui-surface-2);
  padding: 10px;
  cursor: grab;
}
.export-entry-header {
  display: flex;
  align-items: center;
  gap: 8px;
}
.export-entry-header strong {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  overflow-wrap: anywhere;
}
.export-position {
  color: var(--ui-text-muted);
  font-size: 12px;
}
.export-entry-actions {
  display: flex;
  gap: 3px;
  flex-shrink: 0;
}
.export-entry-actions button {
  padding: 4px 7px;
  min-width: 26px;
}
.export-scene-list {
  display: grid;
  gap: 7px;
  margin: 10px 0 0 20px;
  font-size: 12px;
}
.export-scene-list label {
  display: flex;
  align-items: center;
  gap: 7px;
}
.export-scene-list input {
  width: auto;
  margin: 0;
}
.export-scene-list span {
  display: grid;
  gap: 2px;
}
.export-scene-list small,
.export-duplicate {
  color: var(--ui-text-muted);
  font-size: 11px;
}
.export-duplicate {
  display: block;
  margin-top: 7px;
}
</style>
